/**
 * @file 主题定义与应用
 * @description 六套主题的唯一数据源。运行时通过 `applyTheme` 写入 CSS 变量；
 * `src/styles/index.css` 的 `:root` 仅保留默认主题兜底（防首屏未执行 JS 时无色）。
 *
 * @whenToUse 切换主题、读取主题元数据、色卡预览需要各主题独立色值时。
 * @whenNotToUse 业务组件内写死 hex；应使用 `bg-canvas` / `text-primary` 等语义类。
 */

export const themeIds = ['fjord', 'sage', 'birch', 'pine', 'snow', 'ink'] as const

/** 主题 ID */
export type ThemeId = (typeof themeIds)[number]

/** 单套主题的颜色 Token */
export type ThemeTokens = {
  /** 页面底色 */
  canvas: string
  /** 面板 / 侧栏表面 */
  surface: string
  /** 抬升层（输入框、弹层） */
  raised: string
  /** 边框 */
  border: string
  /** 主文案 */
  text: string
  /** 次要文案 */
  muted: string
  /** 主色 */
  primary: string
  /** 主色上的前景（按钮文字等） */
  primaryForeground: string
  /** 强调色 */
  accent: string
  /** 成功 */
  success: string
  /** 警告（琥珀 / 橘黄） */
  warning: string
  /** 危险 */
  danger: string
}

/** 主题完整定义（含展示名） */
export type ThemeDefinition = ThemeTokens & {
  /** 主题 ID */
  id: ThemeId
  /** 展示名称 */
  name: string
  /** 一句话气质说明 */
  tagline: string
}

export const themes: ThemeDefinition[] = [
  {
    id: 'fjord',
    name: 'Fjord Mist',
    tagline: '峡湾雾蓝 · 冷静专业',
    canvas: '#E8EEF2',
    surface: '#F3F6F8',
    raised: '#FFFFFF',
    border: '#C5D0D8',
    text: '#2C3A43',
    muted: '#6B7C88',
    primary: '#4A6B7C',
    primaryForeground: '#FFFFFF',
    accent: '#7FA3B0',
    success: '#6B8F71',
    warning: '#C4922A',
    danger: '#B06A6A',
  },
  {
    id: 'sage',
    name: 'Sage Atelier',
    tagline: '鼠尾草工坊 · 自然柔和',
    canvas: '#E9EDE6',
    surface: '#F4F6F1',
    raised: '#FBFCFA',
    border: '#C9D2C4',
    text: '#2F3A32',
    muted: '#6A776C',
    primary: '#5F7A64',
    primaryForeground: '#FBFCFA',
    accent: '#A3B59A',
    success: '#4F7D5A',
    warning: '#B8862D',
    danger: '#A66B5C',
  },
  {
    id: 'birch',
    name: 'Birch Clay',
    tagline: '白桦粘土 · 温暖人文',
    canvas: '#EDE8E1',
    surface: '#F7F3EE',
    raised: '#FCFAF7',
    border: '#D5CCC0',
    text: '#3A342E',
    muted: '#7A7168',
    primary: '#8B6F5C',
    primaryForeground: '#FCFAF7',
    accent: '#C4A484',
    success: '#6E8B6E',
    warning: '#C4782A',
    danger: '#A35D52',
  },
  {
    id: 'pine',
    name: 'Midnight Pine',
    tagline: '极夜松林 · 深色不闷',
    canvas: '#1C2421',
    surface: '#24302B',
    raised: '#2E3C36',
    border: '#3D4E46',
    text: '#E6EDE8',
    muted: '#9AAFA5',
    primary: '#7BA08A',
    primaryForeground: '#1C2421',
    accent: '#A8C3B0',
    success: '#6F9B78',
    warning: '#E0B35A',
    danger: '#C57B74',
  },
  {
    id: 'snow',
    name: 'Snow Plain',
    tagline: '纯白简约 · 经典亮色',
    canvas: '#FFFFFF',
    surface: '#F7F7F8',
    raised: '#FFFFFF',
    border: '#E5E5E7',
    text: '#141414',
    muted: '#737373',
    primary: '#171717',
    primaryForeground: '#FFFFFF',
    accent: '#A3A3A3',
    success: '#3F8F5F',
    warning: '#D97706',
    danger: '#C45C5C',
  },
  {
    id: 'ink',
    name: 'Ink Night',
    tagline: '偏黑沉静 · 经典暗色',
    canvas: '#0F0F10',
    surface: '#18181B',
    raised: '#222226',
    border: '#2E2E33',
    text: '#F4F4F5',
    muted: '#A1A1AA',
    primary: '#FAFAFA',
    primaryForeground: '#0F0F10',
    accent: '#71717A',
    success: '#4ADE80',
    warning: '#FBBF24',
    danger: '#F87171',
  },
]

/** 按 ID 索引，避免 find 兜底 */
export const themeById: Record<ThemeId, ThemeDefinition> = Object.fromEntries(
  themes.map((theme) => [theme.id, theme]),
) as Record<ThemeId, ThemeDefinition>

/** 默认主题 */
export const defaultThemeId: ThemeId = 'fjord'

/**
 * 按 ID 取主题定义
 * @param id - 主题 ID
 */
export function getTheme(id: ThemeId): ThemeDefinition {
  return themeById[id]
}

/**
 * 判断字符串是否为合法主题 ID（仅用于 localStorage 等外部输入）
 * @param value - 待校验值
 */
export function isThemeId(value: string): value is ThemeId {
  return (themeIds as readonly string[]).includes(value)
}

/**
 * 将主题写入 `html[data-theme]` 与 CSS 变量（唯一运行时着色入口）
 * @param themeId - 目标主题
 */
export function applyTheme(themeId: ThemeId) {
  const theme = getTheme(themeId)
  const root = document.documentElement

  root.dataset.theme = themeId
  root.style.setProperty('--canvas', theme.canvas)
  root.style.setProperty('--surface', theme.surface)
  root.style.setProperty('--raised', theme.raised)
  root.style.setProperty('--border', theme.border)
  root.style.setProperty('--foreground', theme.text)
  root.style.setProperty('--muted', theme.muted)
  root.style.setProperty('--primary', theme.primary)
  root.style.setProperty('--primary-foreground', theme.primaryForeground)
  root.style.setProperty('--accent', theme.accent)
  root.style.setProperty('--success', theme.success)
  root.style.setProperty('--warning', theme.warning)
  root.style.setProperty('--danger', theme.danger)
}
